import { EditIcon, PlusIcon } from '@/assets/icons'
import { iconSizes } from '@/theme/tokens'
import SortableRow from '@components/SortableRow'
import { useDroppable } from '@dnd-kit/core'
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { formatTimeOfDay } from '@lib/datetime'
import { Event } from '@types'
import React from 'react'
import ActionMenu, { ActionMenuItem } from '../ActionMenu'
import { MenuButton } from '../ActionMenu.styles'
import { DayHeader } from '../core/dayHeader'
import { daySlotId } from './daySlots'
import {
	DayHeaderActions,
	DayHeaderBar,
	DayHeaderText,
	DayRoot,
	DaySubtitle,
	DayTitle,
	EmptySlot,
	RowList,
} from './QuickEditPage.styles'

const EmptyDaySlot: React.FC<{ dayIndex: number }> = ({ dayIndex }) => {
	const { setNodeRef, isOver } = useDroppable({ id: daySlotId(dayIndex) })
	return (
		<EmptySlot ref={setNodeRef} $over={isOver}>
			No events
		</EmptySlot>
	)
}

export interface DaySectionProps {
	dayIndex: number
	header: DayHeader
	events: Event[]
	/** The ways to add an event to this day. */
	addItems: ActionMenuItem[]
	/** Open the day's name and notes. */
	onEditDay: () => void
	onDelete: (event: Event) => void
}

/**
 * One Trip day in Quick Edit: its Day header, held at the top while the day
 * scrolls by, with the pencil for its name and notes and the add control; then its events as rows in their own
 * sortable context — or, with nothing planned, the open slot a drag can land on.
 */
const DaySection: React.FC<DaySectionProps> = ({ dayIndex, header, events, addItems, onEditDay, onDelete }) => (
	<DayRoot data-day-index={dayIndex} aria-label={header.title}>
		<DayHeaderBar>
			<DayHeaderText>
				<DayTitle>{header.title}</DayTitle>
				{header.subtitle && <DaySubtitle>{header.subtitle}</DaySubtitle>}
			</DayHeaderText>
			<DayHeaderActions>
				<MenuButton type="button" $strong aria-label={`Edit day name and notes for ${header.title}`} onClick={onEditDay}>
					<EditIcon sx={{ fontSize: iconSizes.md }} />
				</MenuButton>
				<ActionMenu
					label={`Add an event to ${header.title}`}
					icon={<PlusIcon sx={{ fontSize: iconSizes.md }} />}
					strong
					items={addItems}
				/>
			</DayHeaderActions>
		</DayHeaderBar>
		<SortableContext items={events.map(e => e.id)} strategy={verticalListSortingStrategy}>
			{events.length === 0 ? (
				<EmptyDaySlot dayIndex={dayIndex} />
			) : (
				<RowList>
					{events.map(event => (
						<SortableRow
							key={event.id}
							id={event.id}
							name={event.name}
							meta={formatTimeOfDay(event.startTime)}
							removeLabel={`Delete ${event.name}`}
							reorderLabel={`Move ${event.name}`}
							dragAnywhere
							onRemove={() => onDelete(event)}
						/>
					))}
				</RowList>
			)}
		</SortableContext>
	</DayRoot>
)

export default DaySection
